import { Card, Col, Row, Skeleton, Tag } from "antd";
import { Fragment } from "react/jsx-runtime";
import Highcharts from "highcharts";
import "highcharts/modules/drilldown";
import "highcharts/modules/no-data-to-display";
import HighchartsReact from "highcharts-react-official";
import { useEffect, useMemo, useState } from "react";
import { useGetDashboardExctuiveSummaryData } from "features/dashboard/use-cases/get-exctuive-summary";

export const ShopsalesperMonth = ({ dateRange, selectedYear, selectedCategory, selectedEvent, chartRef }: any) => {
    const [selectedMonths, setSelectedMonths] = useState<string[]>([]);
    const monthShortNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sept", "Oct", "Nov", "Dec"];
    const monthFullNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];
    const barColors = [
        '#E55050',
        '#008048',
        '#35B1E8',
        '#FFA51F',
        '#FEBEC8',
        '#4C5167',
        '#008044',
        '#35B1E8',
    ];;
    const monthListPayload = useMemo(() => {
        return selectedMonths
            .map((shortMonth) => monthShortNames.indexOf(shortMonth) + 1)
            .join(",");
    }, [selectedMonths]);

    const handleMonthToggle = (month: string) => {
        setSelectedMonths((prev) =>
            prev.includes(month) ? prev.filter((m) => m !== month) : [...prev, month]
        );
    };

    // ✅ Call hook directly (not wrapped in a function)
    const { data: shopsalesGraph, isFetching: SalesShopisLoading } =
        useGetDashboardExctuiveSummaryData({
            Flag: "shopsales",
            FromDate: dateRange?.[0]?.format("YYYY-MM-DD") || "",
            ToDate: dateRange?.[1]?.format("YYYY-MM-DD") || "",
            monthlist: monthListPayload || undefined,
            Year: selectedYear,
            CategoryId: selectedCategory,
            EventId: selectedEvent
        } as any);

    // ✅ Monthly data from API
    const monthlyDataFromApi =
        shopsalesGraph?.GraphData?.map((item: any) => ({
            name: monthShortNames[item.GMonth - 1],
            y: item.TotalCount,
            revenue: item?.TotalRevenue,
            drilldown: monthFullNames[item.GMonth - 1],
        })) || [];

    // ✅ Drilldown data from API
    const drilldownSeriesFromApi =
        shopsalesGraph?.DrilldownData
            ? Object.values(
                shopsalesGraph.DrilldownData.reduce((acc: any, day: any) => {
                    const monthFull = monthFullNames[day.GMonth - 1];
                    if (!acc[monthFull]) {
                        acc[monthFull] = {
                            id: monthFull,
                            name: `Daily Sales - ${monthFull}`,
                            type: "column",
                            colors: barColors,
                            data: [],
                        };
                    }
                    // acc[monthFull].data.push([
                    //     `${day.GDay} ${monthShortNames[day.GMonth - 1]}`,
                    //     day.TotalRevenue,
                    // ]);
                    acc[monthFull].data.push({
                        name: `${day.GDay} ${monthShortNames[day.GMonth - 1]}`,
                        y: day.TotalCount,          // ✅ use count for the column height
                        revenue: day.TotalRevenue,  // ✅ custom property for tooltip
                    });
                    
return acc;
                }, {})
            )
            : [];

    // ✅ Selected or All
    const baseSeriesData =
        selectedMonths.length === 0
            ? monthlyDataFromApi
            : selectedMonths.map((shortMonth) => {
                const monthIndex = monthShortNames.indexOf(shortMonth);
                const total =
                    shopsalesGraph?.DrilldownData?.filter(
                        (d: any) => d.GMonth - 1 === monthIndex
                    ).reduce((sum: number, d: any) => sum + d.TotalRevenue, 0) || 0;

                return { name: shortMonth, y: total };
            });

    // ✅ Total Revenue
    const totalRevenue =
        shopsalesGraph?.GraphData?.reduce(
            (sum: number, item: any) => sum + (item.TotalRevenue || 0),
            0
        ) || 0;

    const chartOptions: Highcharts.Options = {
        chart: { type: "column" },
        title: {
            text:
                selectedMonths.length === 0
                    ? "Shop Sales per Month (Registration)"
                    : `Combined Sales for: ${selectedMonths.join(", ")}`,
        },
        lang: { noData: "No data found" },
        subtitle: {
            text: `Total Revenue: KWD ${totalRevenue.toLocaleString(undefined, {
                minimumFractionDigits: 2,
            })}`,
            style: {
                fontSize: '9px', // 🔹 set your desired font size
                fontWeight: 'bold', // optional: make it bold
                color: '#333', // optional: set color
            },
        },
        credits: {
            enabled: false
        },
        xAxis: { type: "category" },
        yAxis: { title: { text: "Sales (KWD)" } },
        legend: { enabled: false },
        colors: barColors, // ✅ Apply alternating colors globally
        plotOptions: {
            series: {
                borderWidth: 0,
                colorByPoint: true, // ✅ Alternate colors for bars
                dataLabels: {
                    enabled: true,
                    format: "{point.y:.0f}", // ✅ only top value,
                    style: {
                        color: "black",   // ✅ bar top values in black
                        fontSize: "11px",
                        fontWeight: "bold",
                    },
                },
            },
        },
        tooltip: {
            headerFormat: '<span style="font-size:11px">{series.name}</span><br/>',
            pointFormatter: function (this: any) {
                return `
      <span style="color:${this.color}">${this.name}</span><br/>
      <b>Total Count:</b> ${this.y}<br/>
      <b>Total Revenue:</b> KWD ${(this.revenue || 0)}
    `;
            }
        },
        series: [
            {
                name:
                    selectedMonths.length === 0
                        ? "Monthly Sales"
                        : `Total Sales (${selectedMonths.length} month${selectedMonths.length > 1 ? "s" : ""
                        })`,
                type: "column",
                data: baseSeriesData,
            },
        ],
        ...(selectedMonths.length === 0 && {
            drilldown: {
                series: drilldownSeriesFromApi,
                activeAxisLabelStyle: {
                    textDecoration: "none",
                    color: "black"
                },
                activeDataLabelStyle: {
                    textDecoration: "none",
                    color: "black"
                }
            },
        }),
    } as any;
    useEffect(() => {
        const chart = chartRef.current?.chart;
        if (chart) {
            if (SalesShopisLoading) {
                chart.showLoading("Loading data...");
            } else {
                chart.hideLoading();
            }
        }
    }, [SalesShopisLoading]);

    return (
        <Fragment>

            <Card>
                <Row gutter={[16, 16]} className="month_tabs">
                    {monthShortNames.map((month) => (
                        <Col key={month}>
                            <Tag
                                color={selectedMonths.includes(month) ? "yellow" : "default"}
                                style={{ cursor: "pointer" }}
                                onClick={() => handleMonthToggle(month)}
                            >
                                {month}
                            </Tag>
                        </Col>
                    ))}
                </Row>
                {/* <Spin spinning={SalesShopisLoading} > */}
                {SalesShopisLoading ? <Skeleton /> :
                    <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />}
                {/* </Spin> */}
            </Card>
        </Fragment>
    );
};
